/**
 * OpenWeatherMap weather adapter — implements ProviderAdapter<WeatherParams, RawWeather, WeatherData>.
 *
 * CLIENT / SERVER SPLIT (ADR-002 §5):
 *   CLIENT-SIDE:  paramsSchema, dataShape, cacheKey, glanceView
 *   SERVER-SIDE:  buildRequest, responseSchema, normalize
 *
 * `buildRequest` is THE ONLY place a URL exists in the whole system.
 * API keys are INJECTED as a parameter (never hardcoded, never logged).
 *
 * Identity:
 *   type     = 'weather.current'
 *   provider = 'openweathermap'
 *   tier     = 'free'
 *   ttlSec   = 900 (15-minute free-tier rate)
 */

import { z } from 'zod';
import { weatherParamsSchema, deriveCacheKey, type WeatherParams } from 'briefpane/schema';
import type { GlanceMap } from 'briefpane/schema';
import type { GlanceViewModel, ProviderAdapter, UpstreamRequest } from 'briefpane/contracts';
import { rawWeatherSchema, normalize, type RawWeather, type WeatherData } from './normalize.js';
import { glanceView } from './glance.js';

// ---------------------------------------------------------------------------
// dataShape Zod schema
// Keys must match DATA_SHAPE_KEYS['weather.current']:
//   tempF, tempC, condition, vsYesterdayF, vsYesterdayC, humidityPct, windMph
// ---------------------------------------------------------------------------

const weatherDataShape = z.object({
  tempF: z.number(),
  tempC: z.number(),
  condition: z.string(),
  vsYesterdayF: z.number().nullable(),
  vsYesterdayC: z.number().nullable().optional(),
  humidityPct: z.number(),
  windMph: z.number().nullable(),
});

// ---------------------------------------------------------------------------
// The adapter object (implements ProviderAdapter<WeatherParams, RawWeather, WeatherData>)
// ---------------------------------------------------------------------------

/**
 * The weather adapter with a build-request signature that accepts an injected API key,
 * and a normalize signature that accepts an optional context (for delta computation).
 *
 * This is a slight extension of the base ProviderAdapter interface to allow:
 *   - Key injection without hardcoding (server always calls buildRequest(params, apiKey))
 *   - Optional yesterday context for delta computation
 */
export interface WeatherAdapterShape extends Omit<ProviderAdapter<WeatherParams, RawWeather, WeatherData>, 'buildRequest' | 'normalize'> {
  /** Server-side: build the OWM upstream request, injecting the API key. */
  buildRequest(params: WeatherParams, apiKey: string): UpstreamRequest;
  /** Map normalized data + glance map to the H3 view-model. */
  glanceView(data: WeatherData, glance: GlanceMap): GlanceViewModel;
  /** Normalize OWM raw payload → WeatherData; optional context for delta computation. */
  normalize(raw: RawWeather, context?: { readonly yesterdayTempF?: number }): WeatherData;
}

/**
 * The OpenWeatherMap current-weather adapter.
 *
 * Satisfies H2 (ProviderAdapter) with an extended buildRequest that accepts the
 * API key as a parameter (server-side injection). The registry / Worker injects
 * the key at call time from environment variables — it never appears in any config,
 * log, or client bundle (ADR-002 §5, §7 #1).
 */
export const weatherAdapter: WeatherAdapterShape = {
  // ---- Identity (both halves) ----
  type: 'weather.current',
  provider: 'openweathermap',
  tier: 'free',
  ttlSec: 900,

  // ---- CLIENT-SIDE ----
  paramsSchema: weatherParamsSchema,
  dataShape: weatherDataShape,

  cacheKey: (params: WeatherParams): string =>
    deriveCacheKey({ type: 'weather.current', provider: 'openweathermap', params }),

  glanceView: (data: WeatherData, glance: GlanceMap): GlanceViewModel =>
    glanceView(data, glance),

  // ---- SERVER-SIDE ONLY ----
  responseSchema: rawWeatherSchema,

  /**
   * Build the OWM current-weather request.
   *
   * THE ONLY PLACE A URL EXISTS IN THE SYSTEM (ADR-002 §5, §7 #1).
   * Never call this client-side. The `apiKey` is injected from server env
   * (process.env.OWM_API_KEY in Node; Cloudflare Worker env binding).
   * Never log the returned URL — it contains the key.
   *
   * OWM current-weather API: https://openweathermap.org/current
   * Endpoint: GET api.openweathermap.org/data/2.5/weather
   * Required params: lat, lon, appid (API key), units
   */
  buildRequest: (params: WeatherParams, apiKey: string): UpstreamRequest => ({
    url: `https://api.openweathermap.org/data/2.5/weather?lat=${params.lat}&lon=${params.lon}&units=${params.units}&appid=${apiKey}`,
    method: 'GET',
  }),

  normalize: (raw: RawWeather, context?: { readonly yesterdayTempF?: number }): WeatherData =>
    normalize(raw, context),
};
